import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Label } from '../../components/ui/label';
import { getPolicyVersions, completeHostOnboarding, initStripeConnect, getHostProfile, getStripeConnectStatus } from '../../lib/api';
import { toast } from 'sonner';
import { 
  Loader2, 
  CheckCircle2, 
  FileText, 
  CreditCard,
  ArrowRight,
  Shield,
  ExternalLink
} from 'lucide-react';

export default function HostOnboarding() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [hostProfile, setHostProfile] = useState(null);
  const [policyVersions, setPolicyVersions] = useState({});
  const [stripeStatus, setStripeStatus] = useState(null);
  
  // Policy acceptance states
  const [acceptHostObligations, setAcceptHostObligations] = useState(false);
  const [acceptPayments, setAcceptPayments] = useState(false);
  const [acceptDisclaimer, setAcceptDisclaimer] = useState(false);

  const allPoliciesAccepted = acceptHostObligations && acceptPayments && acceptDisclaimer;

  useEffect(() => {
    const loadData = async () => {
      try {
        const [profileRes, policiesRes, stripeRes] = await Promise.all([
          getHostProfile(),
          getPolicyVersions(),
          getStripeConnectStatus().catch(() => ({ data: null }))
        ]);
        setHostProfile(profileRes.data);
        setPolicyVersions(policiesRes.data);
        setStripeStatus(stripeRes.data);

        // Check if returning from Stripe
        if (searchParams.get('stripe_complete') === '1') {
          toast.success('Stripe account setup complete!');
          // Refresh Stripe status
          const newStripeRes = await getStripeConnectStatus();
          setStripeStatus(newStripeRes.data);
        }

        // If already completed onboarding, redirect to dashboard
        if (profileRes.data?.host_onboarding_completed) {
          navigate('/app/host');
        }
      } catch (error) {
        console.error('Failed to load onboarding data:', error);
        toast.error('Failed to load onboarding data');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [navigate, searchParams]);

  const handleCompleteOnboarding = async () => {
    if (!allPoliciesAccepted) {
      toast.error('Please accept all required policies to continue');
      return;
    }

    setSubmitting(true);
    try {
      // Build accepted policies array
      const acceptedPolicies = [
        { 
          policy_type: 'host_obligations', 
          policy_version: policyVersions.host_obligations || '1.0', 
          context: 'host_onboarding' 
        },
        { 
          policy_type: 'payments', 
          policy_version: policyVersions.payments || '1.0', 
          context: 'host_onboarding' 
        },
        { 
          policy_type: 'platform_disclaimer', 
          policy_version: policyVersions.platform_disclaimer || '1.0', 
          context: 'host_onboarding' 
        }
      ];

      await completeHostOnboarding(acceptedPolicies);
      toast.success('Onboarding complete! Welcome to SWFL Vacation Rentals.');
      navigate('/app/host');
    } catch (error) {
      console.error('Failed to complete onboarding:', error);
      toast.error(error.response?.data?.detail || 'Failed to complete onboarding');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConnectStripe = async () => {
    setSubmitting(true);
    try {
      const response = await initStripeConnect();
      if (response.data.url) {
        // Redirect to Stripe's hosted onboarding
        window.location.href = response.data.url;
      } else if (response.data.onboarding_complete) {
        toast.success('Stripe account already connected!');
        setStripeStatus({ connected: true, payouts_enabled: true, details_submitted: true });
      }
    } catch (error) {
      console.error('Failed to init Stripe Connect:', error);
      toast.error(error.response?.data?.detail || 'Failed to connect with Stripe');
    } finally {
      setSubmitting(false);
    }
  };

  const stripeConnected = stripeStatus?.connected && stripeStatus?.details_submitted;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="host-onboarding-loading">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-8" data-testid="host-onboarding">
      {/* Header */}
      <div className="text-center">
        <h1 className="font-heading text-3xl font-bold text-slate-900 mb-2">
          Complete Your Host Setup
        </h1>
        <p className="text-slate-500">
          Before you can list properties, please review and accept our policies and connect your payment account.
        </p>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center justify-center gap-4">
        <div className={`flex items-center gap-2 ${allPoliciesAccepted ? 'text-green-600' : 'text-slate-400'}`}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${allPoliciesAccepted ? 'bg-green-100' : 'bg-slate-100'}`}>
            {allPoliciesAccepted ? <CheckCircle2 className="w-5 h-5" /> : <FileText className="w-4 h-4" />}
          </div>
          <span className="text-sm font-medium">Policies</span>
        </div>
        <div className="w-12 h-0.5 bg-slate-200" />
        <div className={`flex items-center gap-2 ${stripeConnected ? 'text-green-600' : 'text-slate-400'}`}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${stripeConnected ? 'bg-green-100' : 'bg-slate-100'}`}>
            {stripeConnected ? <CheckCircle2 className="w-5 h-5" /> : <CreditCard className="w-4 h-4" />}
          </div>
          <span className="text-sm font-medium">Payments</span>
        </div>
      </div>

      {/* Policy Acceptance Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary" />
            Host Policies & Agreements
          </CardTitle>
          <CardDescription>
            Please review and accept the following policies to continue as a host on our platform.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Host Obligations */}
          <div className="flex items-start space-x-3 p-4 bg-slate-50 rounded-lg">
            <Checkbox
              id="acceptHostObligations"
              checked={acceptHostObligations}
              onCheckedChange={setAcceptHostObligations}
              data-testid="accept-host-obligations"
            />
            <div className="flex-1">
              <Label 
                htmlFor="acceptHostObligations" 
                className="text-sm font-medium text-slate-900 cursor-pointer"
              >
                Host Obligations Agreement
              </Label>
              <p className="text-sm text-slate-500 mt-1">
                I have read and agree to the{' '}
                <Link to="/host-obligations" target="_blank" className="text-primary hover:underline">
                  Host Obligations
                </Link>{' '}
                including property standards, guest communication, and legal compliance.
              </p>
            </div>
          </div>

          {/* Payments & Payouts Disclosure */}
          <div className="flex items-start space-x-3 p-4 bg-slate-50 rounded-lg">
            <Checkbox
              id="acceptPayments"
              checked={acceptPayments}
              onCheckedChange={setAcceptPayments}
              data-testid="accept-payments-disclosure"
            />
            <div className="flex-1">
              <Label 
                htmlFor="acceptPayments" 
                className="text-sm font-medium text-slate-900 cursor-pointer"
              >
                Payments & Payouts Disclosure
              </Label>
              <p className="text-sm text-slate-500 mt-1">
                I have read and agree to the{' '}
                <Link to="/payments" target="_blank" className="text-primary hover:underline">
                  Payments & Payouts Disclosure
                </Link>{' '}
                including fee structure, payout timing, and tax responsibilities.
              </p>
            </div>
          </div>

          {/* Platform Disclaimer */}
          <div className="flex items-start space-x-3 p-4 bg-slate-50 rounded-lg">
            <Checkbox
              id="acceptDisclaimer"
              checked={acceptDisclaimer}
              onCheckedChange={setAcceptDisclaimer}
              data-testid="accept-platform-disclaimer"
            />
            <div className="flex-1">
              <Label 
                htmlFor="acceptDisclaimer" 
                className="text-sm font-medium text-slate-900 cursor-pointer"
              >
                Platform Disclaimer
              </Label>
              <p className="text-sm text-slate-500 mt-1">
                I have read and agree to the{' '}
                <Link to="/platform-disclaimer" target="_blank" className="text-primary hover:underline">
                  Platform Disclaimer
                </Link>{' '}
                regarding liability limitations and dispute resolution.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stripe Connect Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-primary" />
            Payment Setup
          </CardTitle>
          <CardDescription>
            Connect your bank account via Stripe to receive payouts for your bookings.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {stripeConnected ? (
            <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg">
              <CheckCircle2 className="w-6 h-6 text-green-600" />
              <div className="flex-1">
                <p className="font-medium text-green-900">Stripe Connected</p>
                <p className="text-sm text-green-700">
                  Your payment account is set up and ready to receive payouts.
                  {stripeStatus?.payouts_enabled && ' Payouts are enabled.'}
                </p>
              </div>
              <a 
                href="#" 
                onClick={async (e) => {
                  e.preventDefault();
                  try {
                    const { getStripeDashboardLink } = await import('../../lib/api');
                    const res = await getStripeDashboardLink();
                    if (res.data.url) window.open(res.data.url, '_blank');
                  } catch (err) {
                    toast.error('Failed to open Stripe dashboard');
                  }
                }}
                className="text-sm text-primary hover:underline flex items-center gap-1"
              >
                Dashboard <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                You&apos;ll need to connect a Stripe account to receive payouts. This is a secure process handled by Stripe.
              </p>
              {stripeStatus?.account_id && !stripeStatus?.details_submitted && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-800">
                    You started the Stripe setup but didn&apos;t complete it. Click below to continue.
                  </p>
                </div>
              )}
              <Button 
                onClick={handleConnectStripe}
                disabled={submitting}
                variant="outline"
                className="w-full"
                data-testid="connect-stripe-btn"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <CreditCard className="w-4 h-4 mr-2" />
                )}
                {stripeStatus?.account_id ? 'Continue Stripe Setup' : 'Connect with Stripe'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Complete Button */}
      <div className="flex flex-col items-center gap-4">
        <Button
          onClick={handleCompleteOnboarding}
          disabled={!allPoliciesAccepted || submitting}
          className="btn-primary w-full max-w-md h-12"
          data-testid="complete-onboarding-btn"
        >
          {submitting ? (
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
          ) : (
            <>
              Complete Setup
              <ArrowRight className="w-4 h-4 ml-2" />
            </>
          )}
        </Button>
        {!allPoliciesAccepted && (
          <p className="text-sm text-slate-500">
            Please accept all policies above to continue
          </p>
        )}
      </div>
    </div>
  );
}
